import config from 'config';
import { axiosClient } from 'lib';
import { AppleAuthResponse } from 'react-apple-signin-auth';

import { getToken } from '../helpers';
import { AuthResponse, UserInfo, UserProfile } from '../types';

export const postSignIn = async (
  email: string,
  password: string
): Promise<AuthResponse & { succeeded: boolean }> => {
  const authUserData = await axiosClient.post<{
    payload: AuthResponse & { succeeded: boolean };
  }>(`/api/v${config.apiVersion}/Token/PasswordToken`, {
    password,
    email,
  });

  return authUserData.data.payload;
};

export const getUserInfo = async (token: string): Promise<UserInfo> => {
  const userData = await axiosClient.get<{
    payload: { data: { userInfo: UserInfo } };
  }>(`/api/v${config.apiVersion}/Account/UserInfo`, {
    headers: getToken(token),
  });

  return userData.data.payload.data.userInfo;
};

export const getUserProfile = async (token: string): Promise<UserProfile> => {
  const { data } = await axiosClient.get(
    `/api/v${config.nextApiVersion}/Profile/MyProfile`,
    {
      headers: getToken(token, true),
    }
  );

  return data.payload.profile;
};

export const emailPinRequest = async (payload: { email: string }) => {
  await axiosClient.put(
    `/api/v${config.apiVersionSix}/Account/SendEmailPin`,
    payload
  );
};

export const tempSignIn = async (payload: {
  emailPin: string;
  email: string;
}): Promise<AuthResponse> => {
  const response = await axiosClient.post(
    `/api/v${config.apiVersionSix}/Token/EmailPinToken`,
    payload
  );

  return response.data.payload.data;
};

export const updatePasscode = async ({
  accessToken,
  passcode,
}: {
  accessToken: string;
  passcode: string;
}): Promise<{ message: string; succeeded: boolean }> => {
  const response = await axiosClient.put(
    `/api/v${config.apiVersionSix}/Account/UpdatePasscode`,
    { passcode },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return response.data.payload;
};

type WebRegistrationPayload = {
  firstName: string;
  email: string;
};

export const registerNameAndEmail = async ({
  firstName,
  email,
}: WebRegistrationPayload) => {
  const response = await axiosClient.post(
    `/api/v${config.apiVersion}/Registration/web`,
    { firstName, email }
  );

  return response.data.payload;
};

type CompleteWebRegistrationPayload = {
  passcode: string;
  pincode: string;
  email: string;
  termsAndConditions: boolean;
  department: string | null;
};

export const registerWithFullCredentials = async ({
  passcode,
  pincode,
  termsAndConditions,
  email,
  department,
}: CompleteWebRegistrationPayload) => {
  const response = await axiosClient.post(
    `/api/v${config.apiVersion}/Registration/web/complete`,
    {
      passcode,
      pincode,
      termsAndConditions,
      email,
      department,
    }
  );

  return response.data.payload;
};

type ValidatePinCodePayload = {
  pincode: string;
  email: string;
};

export const checkValidPinCode = async ({
  pincode,
  email,
}: ValidatePinCodePayload) => {
  const response = await axiosClient.post(
    `/api/v${config.apiVersion}/Registration/web/validation/pincode`,
    { pincode, email }
  );

  return response.data.payload;
};

export const googleAuthRequest = async (
  accessToken: string
): Promise<AuthResponse> => {
  const response = await axiosClient.post<{ payload: AuthResponse }>(
    `/api/v6/Registration/google`,
    { accessToken }
  );

  return response.data.payload;
};

export type AppleAuthPayload = AppleAuthResponse & { redirectUri: string };

export const appleAuthRequest = async (
  data: AppleAuthPayload
): Promise<AuthResponse> => {
  const response = await axiosClient.post<{ payload: AuthResponse }>(
    `/api/v6/Registration/apple`,
    data
  );

  return response.data.payload;
};
